import React, { useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { Box, Text, TextField, Button } from '../../../../ui';
import { Dialog, LayoutBox } from '../../../../components';
import { useApi, useAsync } from '../../../../hooks';
import { buildAsIfAopDeds } from './asIfDeductibles';

export const GenerateWorkbookModal = ({ quoteId, open, onClose }) => {
  const { generateWorkbook } = useApi();
  const { control, getValues, reset } = useForm({ defaultValues: { asIfDeds: [] } });
  const { fields, append, remove } = useFieldArray({ control, name: 'asIfDeds' });
  const [warnings, setWarnings] = useState([]);
  const [error, setError] = useState(null);

  const handleClose = () => {
    reset({ asIfDeds: [] });
    setWarnings([]);
    setError(null);
    onClose();
  };

  const generate = async () => {
    setError(null);
    setWarnings([]);
    // Frozen cross-repo contract: request body field is `asIfAopDeds`; response field is `warnings`.
    const body = { asIfAopDeds: buildAsIfAopDeds(getValues('asIfDeds')) };
    try {
      // useApiService throws on any non-2xx; catch it so the failure surfaces inline
      // instead of vanishing into useAsync's (unread) error status.
      const res = await generateWorkbook(quoteId, body);
      const nextWarnings = res && Array.isArray(res.warnings) ? res.warnings : [];
      setWarnings(nextWarnings);
      if (res && res.workbookUrl) {
        window.open(res.workbookUrl, '_blank');
      }
      // Keep the modal open when there are warnings so the underwriter actually sees them.
      if (nextWarnings.length === 0) {
        handleClose();
      }
    } catch (e) {
      setError('Workbook generation failed. Please try again.');
    }
  };

  const { execute, status } = useAsync(generate, { immediate: false });
  const loading = status === 'pending';

  return (
    <Dialog
      open={open}
      heading="Generate Workbook"
      onClose={() => !loading && handleClose()}
      actions={
        <>
          <Button label="Cancel" variant="text" color="$primary" disabled={loading} onPress={handleClose} />
          <Button label="Generate & Download" color="$primary" loading={loading} disabled={loading} onPress={() => execute()} />
        </>
      }
    >
      <LayoutBox gap={12} padY="$1" width="100%">
        <Text size="small" color="$gray.500">
          Optional — model additional AOP deductibles. The quote's deductible (and, on renewals, the expiring deductible) are always included.
        </Text>
        {fields.map((field, index) => (
          <Box key={field.id} flexDirection="row" alignItems="center" gap={8} width="100%">
            <Controller
              control={control}
              name={`asIfDeds.${index}.amount`}
              defaultValue={field.amount || ''}
              render={({ field: f }) => (
                <TextField
                  variant="filled"
                  placeholder="As-if AOP deductible ($)"
                  type="number"
                  value={f.value}
                  onChangeValue={f.onChange}
                  disabled={loading}
                />
              )}
            />
            <Button label="Remove" variant="text" color="$primary" disabled={loading} onPress={() => remove(index)} />
          </Box>
        ))}
        <Button label="Add scenario" variant="text" color="$primary" disabled={loading} onPress={() => append({ amount: '' })} />
        {warnings.length > 0 && (
          <Box gap={4} width="100%" padTop="$1">
            {warnings.map((w, i) => (
              <Text key={i} size="small" color="$orange">
                {w}
              </Text>
            ))}
          </Box>
        )}
        {error && (
          <Text size="small" color="$error">
            {error}
          </Text>
        )}
      </LayoutBox>
    </Dialog>
  );
};
